import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AddGroup from './AddGroup';
import PageNavigator from './PageNavigator';
import CreateButton from './CreateButton';
import { fetchGroups, deleteGroup } from '../services/groupService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import styles from './GroupsTabContent.module.css';

const GROUPS_PER_PAGE = 16;

const GroupsTabContent = ({ selectedTab }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Group permissions
    const { hasAddGroup, hasUpdateGroup, hasDeleteGroup, permissionsLoading } = useCachedPermissions();

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showAddGroup, setShowAddGroup] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(null);

    // Fetch groups when component mounts
    useEffect(() => {
        fetchGroupsData(currentPage);
    }, [currentPage]);

    const fetchGroupsData = async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchGroups(page, GROUPS_PER_PAGE);
            console.log('Groups response:', response);

            if (response.data && response.data.data) {
                setGroups(response.data.data);
                setTotalPages(response.data.last_page || 1);
            } else {
                setGroups([]);
                setTotalPages(1);
            }
        } catch (err) {
            console.error('Error fetching groups:', err);
            setError(err.message);
            setGroups([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };





    const handleAddGroupClose = () => {
        setShowAddGroup(false);
        setEditingGroup(null);
    };

    const handleGroupSave = () => {
        // Refresh the groups list after save
        fetchGroupsData(currentPage);
    };

    const handleCreateGroupClick = () => {
        if (!hasAddGroup) {
            dispatch(showErrorNotification('You do not have permission to create groups'));
            return;
        }
        setEditingGroup(null);
        setShowAddGroup(true);
    };

    const handleEditGroupClick = (group) => {
        if (!hasUpdateGroup) {
            dispatch(showErrorNotification('You do not have permission to edit groups'));
            return;
        }
        setEditingGroup(group);
        setShowAddGroup(true);
    };

    const handleDeleteGroupClick = async (groupId) => {
        if (!hasDeleteGroup) {
            dispatch(showErrorNotification('You do not have permission to delete groups'));
            return;
        }

        if (!window.confirm('Are you sure you want to delete this group?')) {
            return;
        }

        setDeleteLoading(groupId);
        try {
            await deleteGroup(groupId);
            dispatch(showSuccessNotification('Group deleted successfully'));
            // Refresh the groups list
            fetchGroupsData(currentPage);
        } catch (error) {
            console.error('Failed to delete group:', error);
            dispatch(showErrorNotification(error.message || 'Failed to delete group'));
        } finally {
            setDeleteLoading(null);
        }
    };

    // Show AddGroup component
    if (showAddGroup) {
        return (
            <AddGroup
                isOpen={showAddGroup}
                onClose={handleAddGroupClose}
                onSave={handleGroupSave}
                group={editingGroup}
            />
        );
    }

    return (
        <div className={styles.groupsContainer}>
            {/* Header */}
            <div className={styles.headerContainer}>
                <div className={styles.headerLeft}>
                    <button
                        className={styles.backButton}
                        onClick={() => navigate('/customers')}
                        title="Back to Customers"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Back
                    </button>
                    <h2 className={styles.headerTitle}>Groups</h2>
                </div>
                <div className={styles.headerActions}>
                    {hasAddGroup && (
                        <CreateButton label="+ Create Group" onClick={handleCreateGroupClick} />
                    )}
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className={styles.loadingMessage}>
                    <h3>Loading...</h3>
                    <p>Fetching groups...</p>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className={styles.errorMessage}>
                    <h3>Error</h3>
                    <p>{error}</p>
                    <button onClick={() => fetchGroupsData(currentPage)} className={styles.retryButton}>
                        Retry
                    </button>
                </div>
            )}

            {/* Groups Grid */}
            {!loading && !error && groups.length > 0 ? (
                <>
                    <div className={styles.groupsGrid}>
                        {groups.map((group) => (
                            <div key={group._id} className={styles.groupCard}>
                                <div className={styles.groupHeader}>
                                    <div className={styles.groupInfo}>
                                        <h3 className={styles.groupName}>{group.name}</h3>
                                        <p className={styles.groupSwagId}>SWAG ID: {group.swag_id}</p>
                                    </div>
                                    <div className={styles.groupActions}>
                                        {hasUpdateGroup && (
                                            <button
                                                className={styles.actionButton}
                                                onClick={() => handleEditGroupClick(group)}
                                                title="Edit Group"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                </svg>
                                            </button>
                                        )}
                                        {hasDeleteGroup && (
                                            <button
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                onClick={() => handleDeleteGroupClick(group._id)}
                                                disabled={deleteLoading === group._id}
                                                title="Delete Group"
                                            >
                                                {deleteLoading === group._id ? (
                                                    <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                                                    </svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" />
                                                    </svg>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.groupMembers}>
                                    {group.users && group.users.length > 0 ? (
                                        <>
                                            <div className={styles.membersHeader}>
                                                <span className={styles.membersCount}>
                                                    {group.users.length} member{group.users.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            <div className={styles.membersList}>
                                                {group.users.slice(0, 3).map((user) => (
                                                    <div key={user.user_id} className={styles.member}>
                                                        <div className={styles.memberAvatar}>
                                                            {user.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className={styles.memberInfo}>
                                                            <div className={styles.memberName}>{user.name}</div>
                                                            <div className={styles.memberEmail}>{user.email}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {group.users.length > 3 && (
                                                    <div className={styles.moreMembers}>
                                                        +{group.users.length - 3} more
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className={styles.noMembers}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                <circle cx="9" cy="7" r="4" />
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                                            </svg>
                                            <span>No members yet</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Page Navigator */}
                    {totalPages > 1 && (
                        <PageNavigator
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            ) : !loading && !error ? (
                <div className={styles.noGroupsMessage}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <h3>No Groups Found</h3>
                    <p>There are currently no groups to display.</p>
                    <button className={styles.createButton} onClick={handleCreateGroupClick}>
                        Create Your First Group
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export default GroupsTabContent;